<template>
  <div class="container-cardmanage">
    <AppTab v-model="currentTab" :tabs="tabs" />

    <div class="card-con" v-if="isKoreanTabVisible" :class="{ open: currentTab === 'TOSS' }">
      <NoRegisteredCard v-if="!cardStatus.hasToss" @complete-card-add="handleAfterCardAdd('TOSS')" :current-tab />
      <RegisteredCard
        v-if="cardStatus.hasToss"
        :card-info="paymentStore.cardList.TOSS"
        @complete-card-add="handleAfterCardAdd('TOSS')"
        @select-card="handleCardSelect('TOSS')"
        :popup-mode
        :current-tab
      />
    </div>

    <div class="card-con" :class="{ open: currentTab === 'STRIPE' }">
      <NoRegisteredCard v-if="!cardStatus.hasStripe" @add-card="handleStripeCardAdd" :current-tab />
      <RegisteredCard
        v-if="cardStatus.hasStripe"
        :card-info="paymentStore.cardList.STRIPE"
        @add-card="handleStripeCardAdd"
        @select-card="handleCardSelect('STRIPE')"
        :popup-mode
        :current-tab
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import AppTab from '@/components/domains/mobile/common/AppTab.vue';
import NoRegisteredCard from '@/components/domains/self-service/payment-settings/NoRegisteredCard.vue';
import RegisteredCard from '@/components/domains/self-service/payment-settings/RegisteredCard.vue';
import { usePayment } from '@/composables/shared/usePayment';
import { usePaymentStore } from '@/stores/payment';
import { useUserStore } from '@/stores/user';
import { PaymentPopupType, PaymentType, TabItem } from '@/types/components';

const props = withDefaults(
  defineProps<{
    initialTab: PaymentType | null;
    popupMode: PaymentPopupType;
    callback: (cardType?: PaymentType) => void;
  }>(),
  {
    initialTab: 'STRIPE',
    popupMode: 'MANAGE',
  }
);

const userStore = useUserStore();
const paymentStore = usePaymentStore();

const { refetchUserSubscriptionInfo, openStripeCardAddPopup } = usePayment();

const currentTab = ref<PaymentType>('STRIPE');

const tabs = computed(() => {
  const list: TabItem[] = [];

  if (isKoreanTabVisible.value) {
    list.push({ label: 'ss_label_card_korean', value: 'TOSS' });
  }

  list.push({ label: 'card_foreign_some_korean', value: 'STRIPE' });

  return list;
});

const cardStatus = computed(() => ({
  hasToss: paymentStore.cardList.TOSS !== null,
  hasStripe: paymentStore.cardList.STRIPE !== null,
}));

const isKoreanTabVisible = computed<boolean>(() => {
  if (cardStatus.value.hasToss) {
    return true;
  }

  return userStore.userInfo.countryCode === 'KR';
});

const handleStripeCardAdd = async () => {
  const success = await openStripeCardAddPopup();
  if (success) {
    handleCardSelect('STRIPE');
  }
};

const handleAfterCardAdd = async (cardType?: PaymentType) => {
  await refetchUserSubscriptionInfo();

  handleCardSelect(cardType);
};

const handleCardSelect = async (cardType?: PaymentType) => {
  await props.callback(cardType);
};

onMounted(() => {
  if (props.initialTab !== null) {
    currentTab.value = props.initialTab;
    return;
  }
});
</script>

<style scoped lang="scss">
.card-con {
  display: none;
  &.open {
    display: block;
  }
}
</style>
